// src/components/settings/TaxonomyManager.js
// Session 74u. Presentation for the three governed taxonomies. Two surfaces
// consume these — Company Settings (categories, departments) and User
// Management (stations) — so the parts live here rather than being written
// twice and drifting.
//
// Deliberately parts rather than one component with mode flags: categories
// nest two levels, stations group by branch, departments are flat. A single
// component covering all three would be mostly branching.
//
// Every component here is top-level. None is defined inside a parent's body —
// that remounts on every render and drops input focus mid-typing.

import { useState, useEffect } from 'react';
import Overlay from '../Overlay';

const GREEN = '#1D9E75';
const AMBER_BG = '#FFF8EC';
const AMBER_TEXT = '#9A5F00';

export function SectionHeading({ children }) {
  return (
    <p style={{
      fontSize: 11, fontWeight: 500, color: '#9A9A9A', textTransform: 'uppercase',
      letterSpacing: '0.06em', margin: '0 0 10px', paddingBottom: 8,
      borderBottom: '0.5px solid #E0E0E0',
    }}>
      {children}
    </p>
  );
}

export function TaxonomyGroupHeader({ label, meta, onRename, onToggleActive, isActive, busy }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      padding: '9px 12px', background: '#F5F5F3', borderTop: '0.5px solid #E0E0E0',
    }}>
      <span style={{
        fontSize: 13, fontWeight: 700,
        color: isActive === false ? '#9A9A9A' : '#0F0F0F',
        textDecoration: isActive === false ? 'line-through' : 'none',
      }}>
        {label}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {meta && <span style={{ fontSize: 12, color: '#9A9A9A' }}>{meta}</span>}
        {onRename && isActive !== false && <RowAction label="Rename" onClick={onRename} busy={busy} />}
        {onToggleActive && (
          <RowAction
            label={isActive === false ? 'Reactivate' : 'Deactivate'}
            onClick={onToggleActive}
            busy={busy}
          />
        )}
      </span>
    </div>
  );
}

export function TaxonomyRow({ name, meta, isActive, indent, onRename, onToggleActive, busy }) {
  const inactive = isActive === false;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      padding: indent ? '10px 12px 10px 30px' : '10px 12px',
      borderTop: '0.5px solid #E0E0E0', minHeight: 44, boxSizing: 'border-box',
    }}>
      <span style={{ minWidth: 0 }}>
        <span style={{
          fontSize: 13,
          color: inactive ? '#9A9A9A' : '#0F0F0F',
          textDecoration: inactive ? 'line-through' : 'none',
        }}>
          {name}
        </span>
        {/* nowrap so the meta moves to the next line as a WHOLE phrase rather
            than splitting through the middle of itself. Session 78n5: denial
            reasons carry the longest meta any section uses ("Not used yet",
            "Used 11 times") against the longest names, and on a 375px screen
            the row rendered as "Already in stock at / this branch  Not used /
            yet" — three fragments, unreadable. inline-block keeps it a single
            unwrappable unit that simply reflows beneath the name when it does
            not fit. The four older sections have shorter metas and looked fine,
            which is why this was not found until now; they benefit equally. */}
        {meta && (
          <span style={{
            fontSize: 12, color: '#9A9A9A', marginLeft: 8,
            display: 'inline-block', whiteSpace: 'nowrap',
          }}>{meta}</span>
        )}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {onRename && !inactive && <RowAction label="Rename" onClick={onRename} busy={busy} />}
        {onToggleActive && (
          <RowAction
            label={inactive ? 'Reactivate' : 'Deactivate'}
            onClick={onToggleActive}
            busy={busy}
          />
        )}
      </span>
    </div>
  );
}

function RowAction({ label, onClick, busy }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      style={{
        background: 'none', border: 'none', padding: '6px 2px',
        fontSize: 12, fontWeight: 600,
        color: busy ? '#C0C0C0' : '#5C5C5C',
        cursor: busy ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// A parent with no children yet is a real state an admin can create and it is
// unusable until they fix it — items are filed on subcategories, never on the
// parent. Saying so on the row is the only way that is discoverable.
export function EmptyParentNotice() {
  return (
    <div style={{
      padding: '9px 12px 9px 30px', borderTop: '0.5px solid #E0E0E0', background: AMBER_BG,
    }}>
      <span style={{ fontSize: 12, color: AMBER_TEXT }}>
        No subcategories yet — nothing can be filed here until you add one.
      </span>
    </div>
  );
}

export function TaxonomyListShell({ children }) {
  return (
    <div style={{
      border: '0.5px solid #E0E0E0', borderRadius: 10, overflow: 'hidden',
      borderTop: 'none',
    }}>
      {children}
    </div>
  );
}

// Rename and add share one sheet because they are the same interaction with a
// different starting value. `consequence` is a loader rather than a string so
// the caller can count affected rows without this component knowing what a
// department is.
export function TaxonomySheet({
  title,
  confirmLabel,
  initialName = '',
  loadConsequence,
  extraFields,
  onCancel,
  onSubmit,
  saving,
  serverError,
}) {
  const [name, setName] = useState(initialName);
  const [consequence, setConsequence] = useState(null);
  const [localError, setLocalError] = useState('');

  // #224's defect, found at Session 78n2 and NOT introduced by it. This sheet
  // passed an unbranched variant="sheet", so on desktop all three governed
  // taxonomies — categories, departments and units — came up as a full-width
  // band pinned to the bottom of the viewport instead of a centred modal. Raised
  // by Colby against the units sheet; it has been true of the other two since
  // Session 74u.
  //
  // This is the same fix #224 applied to InventoryActionCatalog and #344 to Mark
  // as ordered, in the same shape: the useState/useEffect resize-listener pattern
  // from Session 67, not a plain window.innerWidth read, which never updates when
  // the window is resized.
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!loadConsequence) return undefined;
    loadConsequence().then(result => {
      if (!cancelled) setConsequence(result);
    });
    return () => { cancelled = true; };
  }, [loadConsequence]);

  const error = localError || serverError;

  function handleSubmit() {
    if (!name.trim()) { setLocalError('Enter a name first.'); return; }
    setLocalError('');
    onSubmit(name.trim());
  }

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={onCancel} maxWidth={420}>
      {/* #164, worked around here the same way #224 worked around it rather than
          fixed: Overlay's sheet variant owns its own scroll (maxHeight 90vh +
          overflowY auto), but its modal variant sets overflow:hidden with NO
          maxHeight — so a tall form clips on desktop with no way to reach the
          buttons. This sheet can get tall: a rename shows a consequence box and
          an error box on top of three fields. The child owns the scroll on
          desktop only; the real fix belongs to #164, Sep 8–19. */}
      <div style={{
        padding: 18,
        maxHeight: isMobile ? undefined : '80vh',
        overflowY: isMobile ? undefined : 'auto',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 12 }}>{title}</div>

        <input
          type="text"
          value={name}
          onChange={e => { setName(e.target.value); if (localError) setLocalError(''); }}
          placeholder="Name"
          style={{
            width: '100%', height: 44, border: '1px solid #E0E0E0', borderRadius: 8,
            padding: '0 12px', fontSize: 16, boxSizing: 'border-box', marginBottom: 12,
          }}
        />

        {extraFields}

        {consequence && (
          <div style={{ background: AMBER_BG, borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: AMBER_TEXT, lineHeight: 1.5 }}>{consequence}</div>
          </div>
        )}

        {error && (
          <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 8, border: '1px solid #E0E0E0',
              background: 'white', color: '#5C5C5C', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 8, border: 'none',
              background: saving ? '#E0E0E0' : GREEN,
              color: saving ? '#9A9A9A' : 'white',
              fontSize: 15, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving...' : confirmLabel}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

export function AddButton({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: 10, padding: '9px 16px', borderRadius: 8,
        border: `1.5px solid ${GREEN}`, background: 'white', color: GREEN,
        fontSize: 13, fontWeight: 700, cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// The audit trail surfaced. A trigger-written log nobody can read is logging,
// not governance -- #188/#189 are open precisely because change history existed
// and had no reader.
export function ChangeLogStrip({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{ borderTop: '0.5px solid #E0E0E0', paddingTop: 12, marginTop: 14 }}>
      <p style={{
        fontSize: 11, fontWeight: 500, color: '#9A9A9A', textTransform: 'uppercase',
        letterSpacing: '0.06em', margin: '0 0 8px',
      }}>
        Recent changes
      </p>
      {rows.map(row => (
        <p key={row.id} style={{ fontSize: 12, color: '#5C5C5C', margin: '0 0 4px', lineHeight: 1.5 }}>
          {describeChange(row)}
        </p>
      ))}
    </div>
  );
}

function describeChange(row) {
  const who = row.users?.full_name || 'Someone';
  const when = row.changed_at
    ? new Date(row.changed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';
  if (row.action === 'rename') {
    return `${who} renamed ${row.old_value} to ${row.new_value} · ${when}`;
  }
  if (row.action === 'create') {
    return `${who} added ${row.record_name} · ${when}`;
  }
  return `${who} ${row.action}d ${row.record_name} · ${when}`;
}
